import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./floor-input-draw.module.scss";
import EditorTool from "../../../../elements/editor-tool";
import { makeStyleable } from "../../../../helper/icon-helper";
import IconWall from "../../../../dynamicicons/icon-wall";
import IconEdit from "../../../../dynamicicons/icon-edit";
import IconErase from "../../../../dynamicicons/icon-erase";
import VelavuNotice from "../../../../elements/velavu-notice/velavu-notice";
import VelavuSelect from "../../../../elements/velavu-select";
import PlanToolType from "../../../../state/floor/plan-tool-type";
import ReferenceFloor, {
	referenceFloorToString,
} from "../../../../state/floor/reference-floor";
import { classArr } from "../../../../helper/style-helper";

export interface FloorInputDrawProps {
	className?: string;

	drawTool: PlanToolType;
	onChangeDrawTool: (tool: PlanToolType) => void;

	floors: ReferenceFloor[];
	referenceFloorID: string | null;
	onSelectReferenceFloor?: (floorID: string | null) => void;
}

const SELECT_OPTION_NONE = "";

export default function FloorInputDraw(props: FloorInputDrawProps) {
	const { t } = useTranslation();
	const changeDrawTool = props.onChangeDrawTool;
	const setToolDraw = useCallback(
		() => changeDrawTool("draw"),
		[changeDrawTool],
	);
	const setToolEdit = useCallback(
		() => changeDrawTool("edit"),
		[changeDrawTool],
	);
	const setToolErase = useCallback(
		() => changeDrawTool("erase"),
		[changeDrawTool],
	);

	const propsOnSelectReferenceFloor = props.onSelectReferenceFloor;
	const selectReferenceFloorValue =
		props.referenceFloorID ?? SELECT_OPTION_NONE;
	const onChangeReferenceFloorValue = useCallback(
		(value: string) => {
			if (value === SELECT_OPTION_NONE) {
				propsOnSelectReferenceFloor?.(null);
			} else {
				propsOnSelectReferenceFloor?.(value);
			}
		},
		[propsOnSelectReferenceFloor],
	);

	return (
		<div className={classArr(props.className, styles.content)}>
			<div className={styles.toolbar}>
				<EditorTool
					icon={makeStyleable(IconWall)}
					label={t("dashboard.floor_input.tools.draw")}
					active={props.drawTool === "draw"}
					onClick={setToolDraw}
				/>
				<EditorTool
					icon={makeStyleable(IconEdit)}
					label={t("dashboard.floor_input.tools.edit")}
					active={props.drawTool === "edit"}
					onClick={setToolEdit}
				/>
				<EditorTool
					icon={makeStyleable(IconErase)}
					label={t("dashboard.floor_input.tools.erase")}
					active={props.drawTool === "erase"}
					onClick={setToolErase}
				/>
			</div>

			<VelavuNotice type="info">
				{t("dashboard.floor_input.draw_notice")}
			</VelavuNotice>

			<h2 className={styles.header}>
				{t("dashboard.floor_input.reference_floor")}
			</h2>

			<VelavuSelect
				value={selectReferenceFloorValue}
				onChange={onChangeReferenceFloorValue}
				fullWidth
			>
				{props.floors.map((floor) => (
					<option key={floor.id} value={floor.id}>
						{referenceFloorToString(floor)}
					</option>
				))}

				<option value={SELECT_OPTION_NONE}>{t("common.none")}</option>
			</VelavuSelect>
		</div>
	);
}
